import React from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  Button,
} from "@mui/material";
import { ExpandMore, Add } from "@mui/icons-material";

import { UploadProduct } from "types/products";

export type ManualUploadProps = {
  manual: UploadProduct;
  manualErr: Partial<Record<keyof UploadProduct, string>>;
  onManualChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCreateProduct: () => void;
  defaultExpanded?: boolean;
  disabled?: boolean;
};

export const ManualUpload: React.FC<ManualUploadProps> = ({
  manual,
  manualErr,
  onManualChange,
  onCreateProduct,
  defaultExpanded = false,
  disabled = false,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
          boxShadow: "none",
          border: "1px solid #dee2e6",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: "#f8f9fa",
            "& .MuiAccordionSummary-content": { my: 1.5 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#343a40" }}>
            Or Start with Top Products
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3 }}>
          <Typography
            variant="body1"
            sx={{ color: "#6c757d", mb: 4, lineHeight: 1.6 }}
          >
            Don't have your full catalog? No worries! Add a few products
            manually.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Product Name"
                value={manual.name}
                onChange={onManualChange}
                error={!!manualErr.name}
                helperText={manualErr.name}
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="sku"
                label="SKU (optional)"
                value={manual.sku}
                onChange={onManualChange}
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="amazonId"
                label="ASIN or Amazon URL"
                value={manual.amazonId}
                onChange={onManualChange}
                error={!!manualErr.amazonId}
                helperText={manualErr.amazonId}
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="upc"
                label="UPC (optional)"
                value={manual.upc}
                onChange={onManualChange}
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="brand"
                label="Brand (optional)"
                value={manual.brand}
                onChange={onManualChange}
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="map"
                label="MAP (optional)"
                value={manual.map}
                onChange={onManualChange}
                fullWidth
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <Button
              variant="contained"
              onClick={onCreateProduct}
              startIcon={<Add />}
              disabled={disabled}
              sx={{
                backgroundColor: "#343a40",
                color: "white",
                px: 3,
                py: 1.25,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                "&:hover": { backgroundColor: "#23272b" },
              }}
            >
              Create Product
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
